import React, { useCallback, useEffect, useState, useRef } from "react";
import { Timestamp } from 'firebase/firestore';
import {
    ButtonGroup,
    ContentWrapper,
    FormContainer,
    Divider,
    DescriptionSection,
    Button,
    ActivityNameInput,
    RepoInfoRow,
    RepoNameTag,
    SectionTitle,
    Title,
    CancelButton,
    SubmitButton,
    AddDocButton,
    FetchSummaryWrapper,
    SummaryItem,
    SavingOverlay,
    SavingBox,
    SavingTitle,
    SavingText,
    Spinner,
    LinkGoogleBanner,
    LinkGoogleButton
} from "./index.styles";
import ContributorSection from "./ContributorSection";
import DateTimeSection from "./DateTimeSection";
import FileUploadSection from "./FileUploadSection";
import LabelColorSection from "./LabelColorSection";
import EditReasonPopup from "./EditReasonPopup";
import PRLinkSection from "./PRLinkSection";
import GoogleDocSection from "./GoogleDocSection";
import FigmaLinkSection from "./FigmaLinkSection";
import DiscordDraftSection, { getMessageId } from "./DiscordDraftSection";
import { useAuth } from "../../contexts/AuthContext";
import { MdOutlineAddCircle, MdWarning } from "react-icons/md";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";

import { updateSession, writeNewSession, updateGroupGoogleDocFile, uploadFile, getGroupFigmaFile, updateGroupFigmaFile, updateFileEventId, httpsCallable, functions, auth } from "../../firebase";
import { GoogleAuthProvider, signInWithPopup, linkWithPopup } from "firebase/auth";

function isValidTimeFormat(timeStr) {
    // Expected format: HH:mm (24-hour)
    // HH: 00–23, mm: 00–59
    const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    return regex.test(timeStr);
}

function AddActivityWindow({ onCancel, onCloseSilently, date, onSubmit, isEditMode, sessionData, discordDraftData = null }) {
    const { user, groupMembers, groupInfo, courseInfo, groupActiveRepo, getGoogleAccessToken, ensureGoogleDriveAccess, saveGoogleAccessToken } = useAuth();
    const [selectedContributors, setSelectedContributors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showEditReason, setShowEditReason] = useState(false);
    const [repoMatchHint, setRepoMatchHint] = useState(null);
    const [fetchAllLoading, setFetchAllLoading] = useState(false);
    const fetchAllRunning = useRef(false);
    const groupId = groupInfo.groupId;
    const docRef = useRef(new Map());      // holds actual node refs
    const refCallbacks = useRef(new Map()); // holds stable setter functions
    const prRef = useRef(null);
    const [fetchSummary, setFetchSummary] = useState(null);
    const figmaRef = useRef(new Map());
    const figmaCallbacks = useRef(new Map());
    const [selectedDiscordMessageIds, setSelectedDiscordMessageIds] = useState(new Set());
    const discordDraftInitRef = useRef(null);
    const discordContributorsInitRef = useRef(null);
    const artifacts = courseInfo.artifacts?? {
        discordEnabled: true,
        googleDocsEnabled: true,
        pullRequestsEnabled: true,
        figmaEnabled: true,
        fileEnabled: true,
    };
    const hasAnyArtifactFetchEnabled = artifacts.pullRequestsEnabled || artifacts.googleDocsEnabled || artifacts.figmaEnabled;

    // Google Docs helper function (used in initial formData)
    const newDoc = () => ({ id: crypto.randomUUID?.() || String(Date.now() + Math.random()), link: '', meta: null });
    const [formData, setFormData] = useState({
        activityName: '',
        contributors: [],
        date: date,
        startTime: "",
        endTime: "",
        labelColor: '#FFC2D1',
        description: '',
        files: [],
        isCrossDate: false,
        editTime: 0,
        editHistory: [],
        prLink: '',
        prMeta: null,
        googleDocs: [newDoc()],
        figmaFiles: [{ id: crypto.randomUUID?.() || String(Date.now() + Math.random()), link: '', meta: null }]
    });


    // For gating UI: if there is already data, keep showing even when integration is off
    const hasExistingFiles = (formData.files?.length || 0) > 0;
    const hasExistingPr = !!(formData.prLink || "").trim();
    const hasExistingGoogleDocs = (formData.googleDocs || []).some(
        (d) => (d.link || "").trim() !== ""
    );
    const hasExistingFigma = (formData.figmaFiles || []).some(
        (f) => (f.link || "").trim() !== ""
    );

    const updateFormData = useCallback((field, value) => {
        setFormData(prevData => ({
            ...prevData,
            [field]: value
        }));
    }, []);

    useEffect(() => {
        if (!discordDraftData?.messages?.length) {
            if (!discordDraftData) {
                discordDraftInitRef.current = null;
                discordContributorsInitRef.current = null;
            }
            return;
        }
        const draftKey = `${discordDraftData.messages.length}-${getMessageId(discordDraftData.messages[0], 0)}`;

        if (discordDraftInitRef.current !== draftKey) {
            discordDraftInitRef.current = draftKey;
            setSelectedDiscordMessageIds(
                new Set(discordDraftData.messages.map((m, i) => getMessageId(m, i)))
            );
        }

        // Auto-fill date, startTime, endTime, isCrossDate from Discord messages (read-only, no manual selection)
        if (discordDraftData.date != null || discordDraftData.startTime != null || discordDraftData.endTime != null) {
            setFormData((prev) => ({
                ...prev,
                date: discordDraftData.date ?? prev.date,
                startTime: discordDraftData.startTime ?? prev.startTime,
                endTime: discordDraftData.endTime ?? prev.endTime,
                isCrossDate: discordDraftData.isCrossDate ?? prev.isCrossDate,
            }));
        }

        // Auto-select contributors once per draft: group members whose Discord username appears in the draft
        if (groupMembers?.length > 0 && discordContributorsInitRef.current !== draftKey) {
            discordContributorsInitRef.current = draftKey;
            const discordUsernamesInDraft = new Set(
                discordDraftData.messages
                    .map((m) => (m.author?.username ?? "").trim().toLowerCase())
                    .filter(Boolean)
            );
            const contributorsFromDiscord = groupMembers.filter((m) =>
                discordUsernamesInDraft.has((m.discord_username || "").trim().toLowerCase())
            );
            if (contributorsFromDiscord.length > 0) {
                updateFormData("contributors", contributorsFromDiscord);
                setSelectedContributors(contributorsFromDiscord);
            }
        }
    }, [discordDraftData, groupMembers, updateFormData]);

    const handleDiscordToggleMessage = useCallback((msgId) => {
        setSelectedDiscordMessageIds((prev) => {
            const next = new Set(prev);
            if (next.has(msgId)) next.delete(msgId);
            else next.add(msgId);
            return next;
        });
    }, []);

    const handleDiscordSelectAll = useCallback(() => {
        if (!discordDraftData?.messages?.length) return;
        setSelectedDiscordMessageIds(
            new Set(discordDraftData.messages.map((m, i) => getMessageId(m, i)))
        );
    }, [discordDraftData]);

    const handleDiscordDeselectAll = useCallback(() => {
        setSelectedDiscordMessageIds(new Set());
    }, []);

    const hydrateDocs = (docs) => {
        const arr = Array.isArray(docs) ? docs : Object.values(docs || {});
        return arr.length
            ? arr.map(d => ({
                id: d.id || crypto.randomUUID?.() || String(Date.now() + Math.random()),
                link: d.link || '',
                meta: d.meta && Object.keys(d.meta).length > 0 ? d.meta : null
            }))
            : [newDoc()];
    };

    const runGoogleOAuth = useCallback(async () => {
        const oauthProvider = new GoogleAuthProvider();
        oauthProvider.addScope('https://www.googleapis.com/auth/drive.file');

        const hasGoogleProvider = user?.providerData?.some(p => p.providerId === "google.com");
        if (!hasGoogleProvider) throw new Error("NO_GOOGLE_LINKED");

        const result = await signInWithPopup(auth, oauthProvider);
        const token = GoogleAuthProvider.credentialFromResult(result)?.accessToken;
        if (token) saveGoogleAccessToken(token);
        return token;
    }, [user, saveGoogleAccessToken]);

    const isGoogleUser = user?.providerData?.some(p => p.providerId === "google.com");

    const handleLinkGoogle = useCallback(async () => {
        try {
            const oauthProvider = new GoogleAuthProvider();
            oauthProvider.addScope('https://www.googleapis.com/auth/drive.file');
            const result = await linkWithPopup(user, oauthProvider);
            const token = GoogleAuthProvider.credentialFromResult(result)?.accessToken;
            if (token) saveGoogleAccessToken(token);
        } catch (err) {
            console.error("Failed to link Google account:", err);
        }
    }, [user, saveGoogleAccessToken]);

    const processGoogleDocUploads = async () => {
        const docs = normalizeGoogleDocs(formData.googleDocs);
        if (!docs.length) return [];

        // Ensure we have a valid Google OAuth token with drive.file scope
        let token = await getGoogleAccessToken();
        if (!token) {
            token = await ensureGoogleDriveAccess(user, runGoogleOAuth);
        }

        return Promise.all(
            docs.map(async (doc) => {
                try {
                    const link = doc.link?.trim();
                    if (!link) return { link: "", meta: {} };

                    // Extract Google Doc ID
                    const fileId = link.match(/\/document\/d\/([a-zA-Z0-9_-]+)/)?.[1];
                    if (!fileId) {
                        console.warn("⚠️ Invalid Google Doc link:", link);
                        return { link, meta: doc.meta || {} };
                    }

                    // Export Google Doc as PDF via Drive API (avoids browser CORS restriction)
                    const exportUrl = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=application/pdf`;
                    let response = await fetch(exportUrl, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    // Token expired or insufficient scope — refresh once and retry
                    if (response.status === 401 || response.status === 403) {
                        console.warn("Google token invalid — re-authenticating...");
                        token = await runGoogleOAuth();
                        response = await fetch(exportUrl, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                    }

                    if (!response.ok) {
                        console.error("❌ Failed to export Google Doc PDF:", link, response.status);
                        return { link, meta: doc.meta || {} };
                    }

                    const blob = await response.blob();
                    const fileName = doc.meta?.title
                        ? `${doc.meta.title}.pdf`
                        : `GoogleDoc-${fileId}.pdf`;

                    // Convert to File and upload to Firebase Storage
                    const pdfFile = new File([blob], fileName, { type: "application/pdf" });
                    const uploaded = await uploadFile(pdfFile, groupId, "pending");

                    console.log("📤 Uploaded Google Doc to Firebase:", fileName);

                    // Return stored link + meta + storage info
                    return {
                        link,
                        meta: {
                            ...doc.meta,
                            pdfStorage: uploaded, // store uploaded path
                        },
                    };
                } catch (error) {
                    console.error(`Error uploading Google Doc ${doc.link}:`, error);
                    return { link: doc.link, meta: doc.meta || {} };
                }
            })
        );
    };



    const processFigmaUploads = async () => {
        return Promise.all(
            normalizeFigmaFiles(formData.figmaFiles).map(async (file) => {
                try {
                    const sectionHandle = figmaRef.current.get(file.id);
                    const blob = sectionHandle?.combinedPdf;
                    const meta = sectionHandle?.meta;
                    const link = file.link?.trim();

                    if (!link) return { link: "", meta: {} };

                    if (blob && meta?.fileName) {
                        const pdfFile = new File([blob], `${meta.fileName}.pdf`, { type: "application/pdf" });
                        const uploaded = await uploadFile(pdfFile, groupId, "pending");
                        return { link, meta: { ...meta, pdfStorage: uploaded } };
                    } else {
                        return { link, meta: meta || {} };
                    }
                } catch (error) {
                    console.error(`Error uploading Figma file ${file.id}:`, error);
                    return { link: file.link, meta: file.meta || {} };
                }
            })
        );
    };

    const newFigma = () => ({
        id: crypto.randomUUID?.() || String(Date.now() + Math.random()),
        link: '',
        meta: null
    });

    const hydrateFigmaFiles = (files) =>
        Array.isArray(files) && files.length
            ? files.map(f => ({
                id: f.id || crypto.randomUUID?.() || String(Date.now() + Math.random()),
                link: f.link || '',
                meta: f.meta ?? null
            }))
            : [newFigma()];

    function normalizeGoogleDocs(docs) {
        if (!Array.isArray(docs)) return [];

        return docs.length === 1 && docs[0]?.link.trim() === ''
            ? []
            : docs
                .filter((d) => d.link && d.link.trim() !== "")
                .map(({ link, meta }) => ({
                    link: link.trim(),
                    meta: meta
                }));
    }

    function normalizeFigmaFiles(files) {
        if (!Array.isArray(files)) return [];

        return files.length === 1 && files[0]?.link.trim() === ''
            ? []
            : files.filter((f) => f.link && f.link.trim() !== "").map(({ id, link, meta }) => ({
                id: id || crypto.randomUUID?.() || String(Date.now() + Math.random()), // use for file ref data
                link: link.trim(),
                meta: meta
            }));
    }


    const getDocRef = useCallback((docId) => {
        if (!refCallbacks.current.has(docId)) {
            refCallbacks.current.set(docId, (handle) => {
                if (handle) {
                    // store imperative handle (with fetchNow etc.)
                    docRef.current.set(docId, handle);
                } else {
                    docRef.current.delete(docId);
                }
            });
        }
        return refCallbacks.current.get(docId);
    }, []);

    const getFigmaRef = useCallback((fileId) => {
        if (!figmaCallbacks.current.has(fileId)) {
            figmaCallbacks.current.set(fileId, (handle) => {
                if (handle) {
                    figmaRef.current.set(fileId, handle);
                } else {
                    figmaRef.current.delete(fileId);
                }
            });
        }
        return figmaCallbacks.current.get(fileId);
    }, []);

    useEffect(() => {
        if (isEditMode && sessionData) {
            setFormData({
                ...sessionData,
                activityName: sessionData.title,
                contributors: sessionData.contributors,
                editHistory: sessionData.editHistory || [],
                editTime: sessionData.editTime || 0,
                isCrossDate: sessionData.isCrossDate || false,
                googleDocs: hydrateDocs(sessionData.googleDocs),
                figmaFiles: hydrateFigmaFiles(sessionData.figmaFiles),
            });
            setSelectedContributors(sessionData.contributors);
        }
    }, [isEditMode, sessionData]);


    const fetchAllArtifacts = useCallback(async () => {
        // 1. Guard to prevent multiple executions. This part is correct.
        if (fetchAllRunning.current) {
            return;
        }
        fetchAllRunning.current = true;
        setFetchAllLoading(true);
        setFetchSummary(null);
        try {
            let successes = 0;
            let failures = 0;

            // --- Fetch PR (if link exists) ---
            if (prRef.current?.fetchNow && formData.prLink?.trim()) {
                try {
                    const res = await prRef.current.fetchNow();
                    if (res?.ok) {
                        successes++;
                        setFormData(prev => ({
                            ...prev,
                            prMeta: res.meta
                        }));
                    } else {
                        failures++;
                    }
                } catch (e) {
                    failures++;
                }
            }

            // ========== DOCS CASE ==========
            if (formData.googleDocs[0].link.trim() !== '') {
                // 2. Create a temporary array to hold successful updates.
                const successfulUpdates = [];

                // 3. Iterate sequentially over mounted children components.
                for (const [id, handle] of docRef.current.entries()) {
                    const fetchNow = handle?.fetchNow;

                    if (!fetchNow) {
                        continue;
                    }

                    try {
                        const res = await fetchNow(); // Fetch data for one doc
                        if (res?.ok) {
                            successes++;
                            // 4. If successful, add the result to our temporary array.
                            successfulUpdates.push({ id, meta: res.meta });
                        } else {
                            failures++;
                        }
                    } catch (e) {
                        failures++;
                    }

                    // Optional delay to avoid rate-limiting or UI glitches
                    await new Promise(r => setTimeout(r, 150));
                }

                // 5. After the loop finishes, update the state ONCE with all the collected data.
                if (successfulUpdates.length > 0) {

                    setFormData(prev => {
                        // Create a map for efficient lookups
                        const updatesMap = new Map(successfulUpdates.map(u => [u.id, u.meta]));

                        const updatedDocs = prev.googleDocs.map(doc =>
                            updatesMap.has(doc.id)
                                ? { ...doc, meta: updatesMap.get(doc.id) }
                                : doc
                        );


                        return { ...prev, googleDocs: updatedDocs };
                    });
                }
            }


            // ========== FIGMA CASE ==========
            // Only process if there's at least one non-empty Figma link
            const hasNonEmptyFigmaLink = formData.figmaFiles.some(f => f.link?.trim() !== "");

            if (hasNonEmptyFigmaLink) {
                const successfulFigmaUpdates = [];

                // Iterate through formData.figmaFiles (source of truth) instead of refs
                for (const file of formData.figmaFiles) {
                    const link = file.link?.trim() || "";

                    // Skip empty links
                    if (!link) {
                        continue;
                    }

                    // Look up ref by file.id from formData
                    const handle = figmaRef.current.get(file.id);
                    const fetchNow = handle?.fetchNow;

                    if (!fetchNow) {
                        console.warn(`⚠️ Missing fetchNow for Figma id: ${file.id}`);
                        failures++;
                        continue;
                    }

                    try {
                        const res = await fetchNow();
                        if (res?.ok) {
                            successfulFigmaUpdates.push({ id: file.id, meta: res.meta });
                            successes++;
                        } else {
                            failures++;
                        }
                    } catch (e) {
                        console.error(`❌ Error fetching Figma for ${file.id}:`, e);
                        failures++;
                    }

                    // Small delay between calls
                    await new Promise(r => setTimeout(r, 150));
                }

                // Update formData once with all fetched metadata
                if (successfulFigmaUpdates.length > 0) {
                    setFormData(prev => {
                        const updatesMap = new Map(successfulFigmaUpdates.map(u => [u.id, u.meta]));
                        const updatedFigmaFiles = prev.figmaFiles.map(file =>
                            updatesMap.has(file.id)
                                ? { ...file, meta: updatesMap.get(file.id) }
                                : file
                        );
                        return { ...prev, figmaFiles: updatedFigmaFiles };
                    });
                }
            }

            setFetchSummary({ successes, failures });

        } catch (error) {
            console.error(error);
        } finally {
            // 6. Reset the flags in the 'finally' block to ensure it always runs. This part is also correct.
            fetchAllRunning.current = false;
            setFetchAllLoading(false);
        }
    }, [formData.prLink, formData.googleDocs, formData.figmaFiles]);

    const handleContributorsChange = useCallback((activeContributors) => {
        setSelectedContributors(activeContributors);
        updateFormData('contributors', activeContributors);
    }, [updateFormData]);

    const validateForm = () => {
        //console.log("Current formdata:", formData)
        if (selectedContributors.length === 0) {
            alert('Please select at least one contributor!');
            return false;
        }

        if (formData.activityName.length === 0) {
            alert('Please write an activity name!');
            return false;
        }

        if (!formData.startTime || !formData.endTime) {
            alert('Start time and end time cannot be empty!');
            return false;
        }

        if (!isValidTimeFormat(formData.startTime)) {
            alert('Invalid start time format! Please use HH:mm aa (e.g. 09:30 AM or 02:45 PM).');
            return false;
        }

        if (!isValidTimeFormat(formData.endTime)) {
            alert('Invalid end time format! Please use HH:mm (e.g. 09:30 AM or 02:45 PM)');
            return false;
        }

        if (!formData.isCrossDate && formData.endTime <= formData.startTime) {
            alert('End time cannot be earlier than or equal to start time!');
            return false;
        }

        // If multiple Google Docs, no empties allowed
        const hasDocs = formData.googleDocs.some(doc => doc.link.trim() !== "");
        if (hasDocs) {
            const docsNotFetched = formData.googleDocs.filter(doc =>
                doc.link.trim() !== "" && (!doc.meta || Object.keys(doc.meta).length === 0)
            );
            if (docsNotFetched.length > 0) {
                alert(`Some Google Docs have not been fetched yet.\nPlease click "Fetch" before saving.`);
                return false;
            }
        }

        const emptyDocs = formData.googleDocs.filter(d => d.link.trim() === "");
        if (emptyDocs.length > 1 || (hasDocs > 0 && emptyDocs > 0)) {
            alert("You have extra empty Google Doc rows. Please delete them before saving.");
            return false;
        }

        const hasFigma = formData.figmaFiles.some(f => f.link.trim() !== "");
        if (hasFigma) {
            const figmaNotFetched = formData.figmaFiles.filter(f =>
                f.link.trim() !== "" && (!f.meta || Object.keys(f.meta).length === 0)
            );
            if (figmaNotFetched.length > 0) {
                alert(`Some Figma files have not been fetched yet.\nPlease click "Fetch" before saving.`);
                return false;
            }
        }
        const emptyFigma = formData.figmaFiles.filter(f => f.link.trim() === "");
        if (emptyFigma.length > 1 || (hasFigma > 0 && emptyFigma > 0)) {
            alert("You have extra empty Figma rows. Please delete them before saving.");
            return false;
        }


        // required check for at least one source data
        const hasPR = typeof formData.prLink === 'string' &&
            formData.prLink.trim() !== '' &&
            formData.prMeta &&
            Object.keys(formData.prMeta).length > 0;
        const hasFile = formData.files && formData.files.length > 0;
        const hasDiscord = discordDraftData?.messages?.length > 0 &&
            selectedDiscordMessageIds.size > 0;
        const hasDescription = (formData.description ?? "").trim().length > 0;

        if (!hasPR && !hasFile && !hasDocs && !hasFigma && !hasDiscord && !hasDescription) {
            alert('Please provide at least one of: PR link, file upload, Google Doc, Figma file, or Discord chat with description.');
            return false;
        }

        // If Discord is included, description is required
        if (hasDiscord && !hasDescription) {
            alert('When including Discord chat, a description is required. Please add a description.');
            return false;
        }

        return true;
    };

    const areFilesEqual = (filesA = [], filesB = []) => {
        if (filesA.length !== filesB.length) return false;

        const sortByName = (a, b) => a.name.localeCompare(b.name);
        const fileInfoA = filesA.map(f => ({ name: f.name, size: f.size || null })).sort(sortByName);
        const fileInfoB = filesB.map(f => ({ name: f.name, size: f.size || null })).sort(sortByName);

        return fileInfoA.every((a, i) => {
            const b = fileInfoB[i];
            // ignore size if one side is missing or zero
            return a.name === b.name && (a.size === b.size || !a.size || !b.size);
        });
    };

    // --- Helper: check if doc/figma data is identical ---
    const isSameDocs = (oldDocs = [], newDocs = []) => {
        const normalize = docs =>
            docs
                .filter(d => d.link?.trim() !== "")
                .map(d => ({
                    link: d.link.trim(),
                    revisionId: d.meta?.latestRevision?.revisionId || "",
                }))
                .sort((a, b) => a.link.localeCompare(b.link));

        return JSON.stringify(normalize(oldDocs)) === JSON.stringify(normalize(newDocs));
    };

    const isSameFigma = (oldFiles = [], newFiles = []) => {
        const normalize = files =>
            files
                .filter(f => f.link?.trim() !== "")
                .map(f => ({
                    link: f.link.trim(),
                    versionId: f.meta?.latestVersionId || "",
                }))
                .sort((a, b) => a.link.localeCompare(b.link));

        return JSON.stringify(normalize(oldFiles)) === JSON.stringify(normalize(newFiles));
    };


    const formatFilesList = (files = []) => {
        return files.map(file => file.name).join(', ');
    };

    const getChangedFields = (oldData, newData) => {
        const changes = {};
        const fieldsToCompare = [
            'title', 'date', 'startTime', 'endTime',
            'description', 'labelColor', 'isCrossDate',
            'prLink', 'prMeta', 'googleDocs', 'figmaFiles'
        ];

        fieldsToCompare.forEach(field => {
            const oldValue = field === 'title' ? oldData.title : oldData[field];
            const newValue = field === 'title' ? newData.activityName : newData[field];

            if (field === 'prMeta') {
                const keysToCompare = ['title', 'body', 'commitCount', 'additions', 'deletions', 'changedFiles', 'collaborators', 'coAuthors', 'source', 'state'];
                const diffs = {};

                keysToCompare.forEach(key => {
                    const oldVal = oldValue?.[key] ?? null;
                    const newVal = newValue?.[key] ?? null;
                    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
                        diffs[key] = { from: oldVal, to: newVal };
                    }
                });

                if (Object.keys(diffs).length > 0) {
                    changes[field] = diffs;
                }
            } else if (field === 'googleDocs') {
                const oldDocs = normalizeGoogleDocs(
                    Array.isArray(oldValue) ? oldValue : Object.values(oldValue || {})
                );
                const newDocs = normalizeGoogleDocs(
                    Array.isArray(newValue) ? newValue : Object.values(newValue || {})
                );

                const cleanOld = oldDocs.filter(d => d.link?.trim() !== "");
                const cleanNew = newDocs.filter(d => d.link?.trim() !== "");

                if (cleanOld.length === 0 && cleanNew.length === 0) return;

                // Build comparable summaries (ignore unnecessary meta fields)
                const summarize = docs =>
                    docs.map(d => ({
                        link: d.link?.trim?.() || "",
                        revisionId: d.meta?.latestRevision?.revisionId || "",
                    }));

                const oldSummary = summarize(cleanOld);
                const newSummary = summarize(cleanNew);

                // Normalize order (just in case they were reordered)
                const sortByLink = (a, b) => a.link.localeCompare(b.link);
                oldSummary.sort(sortByLink);
                newSummary.sort(sortByLink);

                // Compare the structural content only
                if (JSON.stringify(oldSummary) !== JSON.stringify(newSummary)) {
                    changes[field] = { from: cleanOld, to: cleanNew };
                }
            } else if (field === "figmaFiles") {
                const oldFigma = normalizeFigmaFiles(
                    Array.isArray(oldValue) ? oldValue : Object.values(oldValue || {})
                );
                const newFigma = normalizeFigmaFiles(
                    Array.isArray(newValue) ? newValue : Object.values(newValue || {})
                );

                const cleanOldFigma = Array.isArray(oldFigma)
                    ? oldFigma.filter(f => f?.link?.trim() !== "")
                    : [];
                const cleanNewFigma = Array.isArray(newFigma)
                    ? newFigma.filter(f => f?.link?.trim() !== "")
                    : [];

                if (cleanOldFigma.length === 0 && cleanNewFigma.length === 0) return;

                // Summarize to key identifiers only (ignore full meta)
                const summarize = files =>
                    files.map(f => ({
                        link: f.link?.trim?.() || "",
                        versionId: f.meta?.latestVersionId || "",
                    }));

                const oldSummary = summarize(cleanOldFigma);
                const newSummary = summarize(cleanNewFigma);

                // Sort by link to ignore order difference
                const sortByLink = (a, b) => a.link.localeCompare(b.link);
                oldSummary.sort(sortByLink);
                newSummary.sort(sortByLink);

                // Compare summaries
                if (JSON.stringify(oldSummary) !== JSON.stringify(newSummary)) {
                    const stripId = arr => arr.map(({ link, meta }) => ({ link, meta }));
                    changes[field] = { from: stripId(cleanOldFigma), to: stripId(cleanNewFigma) };
                }
            } else {
                if (oldValue !== newValue) {
                    changes[field] = {
                        from: oldValue,
                        to: newValue
                    };
                }
            }

        });



        const contributorsChanged = compareContributors(oldData.contributors, newData.contributors);
        if (contributorsChanged) {
            changes.contributors = {
                from: oldData.contributors,
                to: newData.contributors
            };
        }

        if (!areFilesEqual(oldData.files, newData.files)) {
            changes.files = {
                from: formatFilesList(oldData.files),
                to: formatFilesList(newData.files)
            };
        }



        return changes;
    };

    const compareContributors = (oldContributors, newContributors) => {
        if (oldContributors.length !== newContributors.length) {
            return true;
        }

        const sortByEmail = (a, b) => a.email.localeCompare(b.email);
        const sortedOld = [...oldContributors].sort(sortByEmail);
        const sortedNew = [...newContributors].sort(sortByEmail);

        return !sortedOld.every((oldContributor, index) => {
            const newContributor = sortedNew[index];
            return oldContributor.email === newContributor.email;
        });
    };


    const cacheDiscordAttachmentsAndTranscript = async (messages) => {
        if (!Array.isArray(messages) || messages.length === 0) {
            console.log("[Discord cache] No messages to cache.");
            return { messages: messages || [], uploadedFiles: [], transcriptPdf: null };
        }

        console.log("[Discord cache] Starting cache for messages", {
            groupId,
            messageCount: messages.length,
        });

        const urlToFileMeta = new Map();
        const uploadedFiles = [];
        const updatedMessages = [];
        const failedUrls = new Set();

        for (const msg of messages) {
            const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];

            const processedAttachments = await Promise.all(
                attachments.map(async (att) => {
                    const originalUrl = att.url || att.proxyURL || att.proxyUrl;

                    if (!originalUrl) return att;

                    if (urlToFileMeta.has(originalUrl)) {
                        const cached = urlToFileMeta.get(originalUrl);

                        return {
                            ...att,
                            url: cached.url,
                            originalUrl,
                            storageMeta: cached,
                        };
                    }

                    let fileMeta = urlToFileMeta.get(originalUrl);

                    if (!fileMeta) {
                        try {
                            const response = await fetch(originalUrl);

                            if (!response.ok) {
                                throw new Error(`Failed to fetch: ${response.status}`);
                            }

                            const blob = await response.blob();

                            const baseName =
                                att.name ||
                                att.filename ||
                                originalUrl.split("/").pop()?.split("?")[0] ||
                                "discord-attachment";

                            const fileType =
                                blob.type || att.contentType || "application/octet-stream";

                            const file = new File([blob], baseName, { type: fileType });

                            const uploaded = await uploadFile(file, groupId, "pending");

                            fileMeta = uploaded;

                            urlToFileMeta.set(originalUrl, uploaded);

                            uploadedFiles.push(uploaded);
                        } catch (err) {
                            console.error("[Discord cache] Upload error", originalUrl, err);
                            failedUrls.add(originalUrl);
                            return att;
                        }
                    }

                    return {
                        ...att,
                        url: fileMeta.url,
                        originalUrl,
                        storageMeta: fileMeta,
                    };
                })
            );

            updatedMessages.push({
                ...msg,
                attachments: processedAttachments,
            });
        }

        if (failedUrls.size > 0) {
            try {
                const cacheFn = httpsCallable(functions, "cacheDiscordAttachments");
                const items = Array.from(failedUrls).map((url) => ({
                    url,
                    name: url.split("/").pop()?.split("?")[0] || "discord-attachment",
                }));
                const res = await cacheFn({ groupId, items });
                const results = Array.isArray(res?.data?.results) ? res.data.results : [];
                console.log("[Discord cache] Fallback results from server", {
                    requested: items.length,
                    received: results.length,
                });
                const mapping = {};

                results.forEach((r) => {
                    if (r && r.originalUrl && r.storage) {
                        mapping[r.originalUrl] = r.storage;
                        urlToFileMeta.set(r.originalUrl, r.storage);
                        uploadedFiles.push(r.storage);
                    }
                });

                if (Object.keys(mapping).length > 0) {
                    for (let i = 0; i < updatedMessages.length; i += 1) {
                        const msg = updatedMessages[i];
                        const atts = Array.isArray(msg.attachments) ? msg.attachments : [];
                        const replaced = atts.map((att) => {
                            const originalUrl =
                                att.originalUrl || att.url || att.proxyURL || att.proxyUrl;
                            const storage = originalUrl ? mapping[originalUrl] : null;
                            if (!storage) return att;
                            return {
                                ...att,
                                url: storage.url,
                                originalUrl,
                                storageMeta: storage,
                            };
                        });
                        updatedMessages[i] = {
                            ...msg,
                            attachments: replaced,
                        };
                    }
                }
            } catch (err) {
                console.error("[Discord cache] Fallback cacheDiscordAttachments failed", err);
            }
        }

        console.log("[Discord cache] Done", {
            messageCount: updatedMessages.length,
            uploadedCount: uploadedFiles.length,
        });

        return {
            messages: updatedMessages,
            uploadedFiles,
            transcriptPdf: null,
        };
    };

    const submitNewSession = async () => {
        try {
            const figmaUploads = await processFigmaUploads();
            const googleDocUploads = await processGoogleDocUploads();

            const session = {
                title: formData.activityName,
                contributors: formData.contributors,
                date: formData.date,
                startTime: formData.startTime,
                endTime: formData.endTime,
                labelColor: formData.labelColor,
                description: formData.description,
                created_at: Timestamp.now(),
                created_by: user.email,
                isCrossDate: formData.isCrossDate,
                files: formData.files,
                prLink: formData.prLink,
                prMeta: formData.prMeta,
                figmaFiles: figmaUploads?.length ? figmaUploads : [],
                googleDocs: googleDocUploads?.length ? googleDocUploads : [],
                id: `${user.email}-${Timestamp.now().seconds}-${Timestamp.now().nanoseconds}`,
                editTime: 0,
                editHistory: []
            };
            let discordUploads = [];
            if (discordDraftData) {
                const messages = discordDraftData.messages ?? [];
                const selectedMessages = messages.filter((m, i) =>
                    selectedDiscordMessageIds.has(getMessageId(m, i))
                );
                const {
                    messages: cachedMessages,
                    uploadedFiles,
                } = await cacheDiscordAttachmentsAndTranscript(selectedMessages);
                discordUploads = uploadedFiles || [];
                session.discord = {
                    messages: cachedMessages,
                };
            }

            const new_session = await writeNewSession(session, user, formData.files, groupId);

            for (const doc of googleDocUploads) {
                const pdfFileMeta = doc.meta?.pdfStorage;
                if (pdfFileMeta?.fileId) {
                    await updateFileEventId(pdfFileMeta.fileId, new_session.eventId);
                }
            }

            for (const figma of figmaUploads) {
                const pdfFileMeta = figma.meta?.pdfStorage;
                if (pdfFileMeta?.fileId) {
                    await updateFileEventId(pdfFileMeta.fileId, new_session.eventId);
                }
            }

            for (const uploaded of discordUploads) {
                if (uploaded?.fileId) {
                    await updateFileEventId(uploaded.fileId, new_session.eventId);
                }
            }

            if (onSubmit) {
                // Update Google Doc revision info to group
                // Use googleDocUploads (processed data) for new sessions
                if (Array.isArray(googleDocUploads)) {
                    for (const d of googleDocUploads) {
                        const fileId = d.link.match(/\/document\/d\/([a-zA-Z0-9_-]+)/)?.[1];
                        const wordCount = d.meta?.wordCount;

                        if (!fileId || wordCount === null || wordCount === undefined) {
                            continue;
                        }

                        // Update word count for this file
                        await updateGroupGoogleDocFile(groupId, fileId, wordCount);
                    }
                }

                // Use figmaUploads (processed data) for new sessions
                if (Array.isArray(figmaUploads)) {
                    for (const f of figmaUploads) {
                        const fileId = f.link.match(/figma\.com\/(?:file|design|board|slide|proto)\/([a-zA-Z0-9]+)\b/)?.[1];
                        const latestVersionId = f.meta?.latestVersionId;

                        if (!fileId || !latestVersionId) continue;

                        const fileCheckRes = await getGroupFigmaFile(groupId, fileId);

                        const alreadyHasVersion =
                            fileCheckRes.found &&
                            Object.values(fileCheckRes.versionIds || {}).includes(String(latestVersionId));

                        if (!alreadyHasVersion) {
                            await updateGroupFigmaFile(groupId, fileId, latestVersionId, f.meta?.pdfUrls, f.meta?.fileName, f.meta?.lastModified);
                        }
                    }
                }
                onSubmit(new_session);

            }
            onCloseSilently?.();
        } catch (error) {
            console.error("Error creating new session:", error);
            alert("An error occurred while creating the session.");
        }
    };

    const handleEditReasonSubmit = async (reason) => {
        setIsSubmitting(true);
        try {
            const now = Timestamp.now();

            // Compare existing sessionData vs new formData
            const sameDocs = isSameDocs(sessionData.googleDocs, formData.googleDocs);
            const sameFigma = isSameFigma(sessionData.figmaFiles, formData.figmaFiles);
            // Only upload if something changed
            let cleanedGoogleDocs = formData.googleDocs || [];
            let cleanedFigmaFiles = formData.figmaFiles || [];
            if (!sameDocs) {
                cleanedGoogleDocs = await processGoogleDocUploads();
            } else {
                cleanedGoogleDocs = formData.googleDocs.map(doc => {
                    const originalDoc = sessionData.googleDocs?.find(d => d.link === doc.link);
                    if (!doc.meta?.pdfStorage && originalDoc?.meta?.pdfStorage) {
                        return {
                            ...doc,
                            meta: { ...doc.meta, pdfStorage: originalDoc.meta.pdfStorage },
                        };
                    }
                    return doc;
                });
            }
            if (!sameFigma) {
                cleanedFigmaFiles = await processFigmaUploads();
            } else {
                {
                    cleanedFigmaFiles = formData.figmaFiles.map(file => {
                        const originalFigma = sessionData.figmaFiles?.find(f => f.link === file.link);
                        if (!file.meta?.pdfStorage && originalFigma?.meta?.pdfStorage) {
                            return {
                                ...file,
                                meta: { ...file.meta, pdfStorage: originalFigma.meta.pdfStorage },
                            };
                        }
                        return file;
                    });
                }
            }

            //Remove IDs and empty link
            cleanedGoogleDocs = cleanedGoogleDocs.map(({ id, ...rest }) => rest).filter(
                (d) => d.link && d.link.trim() !== ""
            );
            cleanedFigmaFiles = cleanedFigmaFiles.map(({ id, ...rest }) => rest).filter(
                (d) => d.link && d.link.trim() !== ""
            );


            const enrichedFormData = {
                ...formData,
                googleDocs: cleanedGoogleDocs,
                figmaFiles: cleanedFigmaFiles
            };
            const changes = getChangedFields(sessionData, enrichedFormData);

            if (Object.keys(changes).length === 0) {
                alert("No changes were made to the session.");
                setShowEditReason(false);
                setIsSubmitting(false);
                return;
            }

            const editRecord = {
                timestamp: now,
                editor: user.email,
                reason: reason,
                changes: changes,
            };

            const updatedSession = {
                title: enrichedFormData.activityName,
                contributors: enrichedFormData.contributors,
                date: enrichedFormData.date,
                startTime: enrichedFormData.startTime,
                endTime: enrichedFormData.endTime,
                labelColor: enrichedFormData.labelColor,
                description: enrichedFormData.description,
                created_at: now,
                created_by: user.email,
                isCrossDate: enrichedFormData.isCrossDate,
                files: enrichedFormData.files,
                prLink: enrichedFormData.prLink,
                prMeta: enrichedFormData.prMeta,
                figmaFiles: cleanedFigmaFiles?.length ? cleanedFigmaFiles : [],
                googleDocs: cleanedGoogleDocs?.length ? cleanedGoogleDocs : [],
                id: sessionData.id,
                editTime: (enrichedFormData.editTime || 0) + 1,
                editHistory: [...(enrichedFormData.editHistory || []), editRecord],
            };

            const new_session = await updateSession(
                sessionData.eventId,
                updatedSession,
                user,
                enrichedFormData.files
            );

            if (new_session?.ok === false && new_session.code === "PERMISSION_DENIED") {
                alert("❗ You don’t have permission to update this session.\nOnly contributors listed on the card can edit it.");
                setShowEditReason(false);
                setIsSubmitting(false);
                return;
            }

            if (new_session?.ok === false) {
                console.error("Firestore error:", new_session.message);
                alert(new_session.message || "An unknown error occurred while updating the session.");
                setIsSubmitting(false);
                setShowEditReason(false);
                return;
            }

            for (const doc of cleanedGoogleDocs) {
                const pdfFileMeta = doc?.meta?.pdfStorage;
                if (!pdfFileMeta?.fileId) continue;

                const originalDoc = sessionData.googleDocs?.find(d =>
                    d.meta?.pdfStorage?.fileId === pdfFileMeta.fileId
                );

                // Only update if the doc is new or its revision ID changed
                const oldRevision = originalDoc?.meta?.latestRevision?.revisionId;
                const newRevision = doc?.meta?.latestRevision?.revisionId;

                if (!originalDoc || oldRevision !== newRevision) {
                    await updateFileEventId(pdfFileMeta.fileId, sessionData.eventId);
                }
            }

            for (const figma of cleanedFigmaFiles) {
                const pdfFileMeta = figma?.meta?.pdfStorage;
                if (!pdfFileMeta?.fileId) continue;

                const originalFigma = sessionData.figmaFiles?.find(f =>
                    f.meta?.pdfStorage?.fileId === pdfFileMeta.fileId
                );

                // Only update if new or version ID changed
                const oldVersion = originalFigma?.meta?.latestVersionId;
                const newVersion = figma?.meta?.latestVersionId;

                if (!originalFigma || oldVersion !== newVersion) {
                    await updateFileEventId(pdfFileMeta.fileId, sessionData.eventId);
                }
            }

            if (onSubmit) {
                // Update Google Doc revision info to group
                // Use cleanedGoogleDocs (final saved data) instead of formData.googleDocs
                if (Array.isArray(cleanedGoogleDocs)) {
                    for (const d of cleanedGoogleDocs) {
                        const fileId = d.link.match(/\/document\/d\/([a-zA-Z0-9_-]+)/)?.[1];
                        const wordCount = d.meta?.wordCount;

                        if (!fileId || wordCount === null || wordCount === undefined) {
                            continue;
                        }

                        // Update word count for this file
                        await updateGroupGoogleDocFile(groupId, fileId, wordCount);
                    }
                }

                // Use cleanedFigmaFiles (final saved data) instead of formData.figmaFiles
                if (Array.isArray(cleanedFigmaFiles)) {
                    for (const f of cleanedFigmaFiles) {
                        const fileId = f.link.match(/figma\.com\/(?:file|design|board|slide|proto)\/([a-zA-Z0-9]+)\b/)?.[1];
                        const latestVersionId = f.meta?.latestVersionId;

                        if (!fileId || !latestVersionId) continue;

                        const fileCheckRes = await getGroupFigmaFile(groupId, fileId);

                        const alreadyHasVersion =
                            fileCheckRes.found &&
                            Object.values(fileCheckRes.versionIds || {}).includes(String(latestVersionId));

                        if (!alreadyHasVersion) {
                            await updateGroupFigmaFile(groupId, fileId, latestVersionId, f.meta?.pdfUrls, f.meta?.fileName, f.meta?.lastModified);
                        }
                    }
                }
                onSubmit(new_session);

            }
            onCloseSilently?.();
        } catch (frontendError) {
            // catches only *frontend runtime issues*, not Firebase logic
            console.error("Frontend error while updating session:", frontendError);
            alert(`Something went wrong in the app:\n${frontendError.message || frontendError}`);
        } finally {
            setIsSubmitting(false);
            setShowEditReason(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        //console.log("Submitting form data is triggered");

        if (!validateForm()) return;

        if (isEditMode) {
            setShowEditReason(true);
        } else {
            setIsSubmitting(true);
            await submitNewSession();
            setIsSubmitting(false);
        }
    };

    const handleDescriptionChange = (e) => {
        updateFormData('description', e.target.value);
    };

    const handleFilesChange = useCallback((files) => {
        updateFormData('files', files);
    }, [updateFormData]);

    const handlePRLinkChange = useCallback((link) => {
        updateFormData('prLink', link);
    }, [updateFormData]);

    const handlePRMetaFetched = useCallback((meta) => {
        updateFormData('prMeta', meta);

        // Auto-fill contributors if PR collaborators match group members
        if ((meta?.collaborators?.length > 0 || meta?.coAuthors?.length > 0) && groupMembers?.length > 0) {
            const allHandles = new Set([
                ...(meta.collaborators || []),
                ...(meta.coAuthors || []),
            ]);

            // Match GitHub usernames with group members
            const contributorsFromPR = groupMembers.filter(m =>
                allHandles.has(m.github_username)
            );
            updateFormData('contributors', contributorsFromPR);
            setSelectedContributors(contributorsFromPR);
        }
    }, [updateFormData, groupMembers]);

    // Google Doc methods
    const addGoogleDoc = useCallback(() => {
        setFormData(prev => ({ ...prev, googleDocs: [...prev.googleDocs, newDoc()] }));
    }, []);


    const removeGoogleDoc = useCallback((docId) => {
        setFormData(prev => {
            const current = prev.googleDocs ?? [];

            // if only one row: reset it to blank instead of removing
            if (current.length <= 1) {
                const reset = [{ ...newDoc() }];
                return { ...prev, googleDocs: reset };
            }

            // otherwise remove normally
            const updated = current.filter((_, i) => i !== docId);
            return { ...prev, googleDocs: updated };
        });
    }, []);

    const updateGoogleDocLink = useCallback((docId, link) => {
        const cleaned = (link || '').split('?')[0].split('#')[0].replace(/\/edit$/, '');
        setFormData(prev => {
            const updated = [...prev.googleDocs];
            updated[docId] = { ...updated[docId], link: cleaned };
            return { ...prev, googleDocs: updated };
        });
    }, []);

    const updateGoogleDocMeta = useCallback((docId, meta) => {
        setFormData(prev => {
            const updated = [...prev.googleDocs];
            updated[docId] = { ...updated[docId], meta };
            return { ...prev, googleDocs: updated };
        });
    }, []);

    const onDocChange = useCallback((docId, val) => {
        updateGoogleDocLink(docId, val);
    }, [updateGoogleDocLink]);

    const onMetaFetched = useCallback((docId, meta) => {
        updateGoogleDocMeta(docId, meta);
    }, [updateGoogleDocMeta]);

    const onDocRemove = useCallback((docId) => {
        removeGoogleDoc(docId);
    }, [removeGoogleDoc]);

    const isDuplicateDoc = useCallback((docId, link) => {
        if (!link) return false;
        return formData.googleDocs.some((d, i) => i !== docId && d.link === link);
    }, [formData.googleDocs]);

    const updateFigmaLink = useCallback((fileId, link) => {
        const cleaned = (link || "").trim().split("?")[0].split("#")[0];
        const match = cleaned.match(/figma\.com\/(?:design|board|slide|proto|file)\/([a-zA-Z0-9]+)\b/);
        const normalized = match ? cleaned : (link || "").trim();

        setFormData(prev => {
            const files = Array.isArray(prev.figmaFiles) ? prev.figmaFiles : [];
            const idx = files.findIndex(f => f.id === fileId);

            if (idx >= 0) {
                // Update existing file
                const updated = [...files];
                updated[idx] = { ...updated[idx], link: normalized };
                //console.log("[updateFigmaLink] ✅ Updated id:", fileId, "at index:", idx, "link:", normalized);
                return { ...prev, figmaFiles: updated };
            } else {
                // Self-heal: add new file if not found
                //console.log("[updateFigmaLink - self-heal] ⚠️ Adding new id:", fileId, "link:", normalized);
                return { ...prev, figmaFiles: [...files, { id: fileId, link: normalized, meta: null }] };
            }
        });
    }, []);

    const updateFigmaMeta = useCallback((fileId, meta) => {
        setFormData(prev => {
            const updated = prev.figmaFiles.map(file =>
                file.id === fileId ? { ...file, meta } : file
            );
            return { ...prev, figmaFiles: updated };
        });
    }, []);

    const handleFigmaMetaFetched = useCallback((fileId, meta) => {
        updateFigmaMeta(fileId, meta);

        if (!meta || !groupMembers?.length) return;

        const editorNames = Array.isArray(meta.editors)
            ? meta.editors
            : Object.keys(meta.editors);

        const allEditors = new Set(
            editorNames.map(name => String(name || "").toLowerCase())
        );

        if (allEditors.size === 0) return;

        // Match group members (figma_handle / name / email)
        const contributorsFromFigma = groupMembers.filter(m =>
            allEditors.has(String(m.figma?.handle || "").toLowerCase())
        );
        // Update contributors in form
        if (contributorsFromFigma.length > 0) {
            updateFormData("contributors", contributorsFromFigma);
            setSelectedContributors(contributorsFromFigma);
        }

    }, [updateFigmaMeta, groupMembers, updateFormData]);


    return (
        <FormContainer onSubmit={handleSubmit}>
            <Title>
                <ActivityNameInput
                    type="text"
                    placeholder="Write an activity name"
                    value={formData.activityName}
                    onChange={(e) => updateFormData('activityName', e.target.value)}
                />
            </Title>
            <Divider />
            <ContentWrapper>
                <SectionTitle>Contributors</SectionTitle>
                <ContributorSection
                    contributors={groupMembers}
                    selectedContributors={selectedContributors}
                    onContributorsChange={handleContributorsChange}
                />
                <SectionTitle>Date & Time</SectionTitle>
                <DateTimeSection
                    initialDate={formData.date}
                    initialStartTime={formData.startTime}
                    initialEndTime={formData.endTime}
                    onDateChange={(date) => updateFormData('date', date)}
                    onStartTimeChange={(time) => updateFormData('startTime', time)}
                    onEndTimeChange={(time) => updateFormData('endTime', time)}
                    onCrossDateChange={(isCrossDate) => updateFormData('isCrossDate', isCrossDate)}
                    isEditMode={isEditMode}
                    initialCrossDate={formData.isCrossDate}
                />
                <SectionTitle>Label Color</SectionTitle>
                <LabelColorSection
                    initialColor={formData.labelColor}
                    onColorChange={(color) => updateFormData('labelColor', color)}
                />
                {discordDraftData && artifacts.discordEnabled && (
                    <>
                        <SectionTitle>Discord chat</SectionTitle>
                        <DiscordDraftSection
                            discordDraft={{
                                channelName: discordDraftData.channelName ?? discordDraftData.channelId,
                                channelId: discordDraftData.channelId,
                                messages: discordDraftData.messages ?? [],
                            }}
                            maxHeight={240}
                            selectable={true}
                            selectedMessageIds={selectedDiscordMessageIds}
                            onToggleMessage={handleDiscordToggleMessage}
                            onSelectAll={handleDiscordSelectAll}
                            onDeselectAll={handleDiscordDeselectAll}
                            groupMembers={groupMembers}
                        />
                    </>
                )}
                <SectionTitle>Description</SectionTitle>
                <DescriptionSection
                    placeholder={discordDraftData ? "Add a description for the Discord chat" : "Add optional notes"}
                    value={formData.description}
                    onChange={handleDescriptionChange}
                />
                {(artifacts.fileEnabled || hasExistingFiles) && (
                    <>
                        <SectionTitle>Upload Files</SectionTitle>
                        <FileUploadSection currentFiles={formData.files} onFilesChange={handleFilesChange} />
                    </>
                )}
                {(artifacts.pullRequestsEnabled || hasExistingPr) && (
                    <SectionTitle>PR Link</SectionTitle>
                )}
                {groupActiveRepo?.repo?.full_name && repoMatchHint && (
                    <RepoInfoRow>
                        <span>Current Active Repo:</span>
                        {groupActiveRepo?.repo?.full_name && (
                            <RepoNameTag $match={repoMatchHint === "match"}>
                                {groupActiveRepo.repo.full_name}
                            </RepoNameTag>
                        )}
                    </RepoInfoRow>
                )}
                {(artifacts.pullRequestsEnabled || hasExistingPr) && (
                    <PRLinkSection
                        ref={prRef}
                        prLink={formData.prLink}
                        initialMeta={formData.prMeta}
                        onChange={handlePRLinkChange}
                        onMetaFetched={handlePRMetaFetched}
                        updateFormData={updateFormData}
                        setRepoMatchHint={setRepoMatchHint}
                        onFetchStart={() => setFetchSummary(null)}
                    />
                )}
                {(artifacts.googleDocsEnabled || hasExistingGoogleDocs) && (
                    <>
                        <SectionTitle>
                            Google Doc Link
                            <AddDocButton type="button" onClick={addGoogleDoc}>
                                <span className="icon"><MdOutlineAddCircle /></span>
                                <span>Add</span>
                            </AddDocButton>
                        </SectionTitle>

                        {!isGoogleUser && (
                            <LinkGoogleBanner>
                                <MdWarning size={16} />
                                <span>Link your Google account to enable doc uploads.</span>
                                <LinkGoogleButton type="button" onClick={handleLinkGoogle}>
                                    Link now
                                </LinkGoogleButton>
                            </LinkGoogleBanner>
                        )}

                        {formData.googleDocs.map((doc, idx) => (
                            <GoogleDocSection
                                key={doc.id}
                                ref={getDocRef(doc.id)}
                                docId={idx}
                                googleDocLink={doc.link}
                                initialMeta={doc.meta}
                                onChange={onDocChange}
                                onMetaFetched={onMetaFetched}
                                onRemove={onDocRemove}
                                isDuplicate={isDuplicateDoc(idx, doc.link)}
                                canRemove={formData.googleDocs.length > 1}
                                isEmpty={doc.link.trim() === ""}
                                onFetchStart={() => setFetchSummary(null)}
                            />
                        ))}
                    </>
                )}

                {(artifacts.figmaEnabled || hasExistingFigma) && (
                    <>
                        <SectionTitle>
                            Figma File Link
                            <AddDocButton
                                type="button"
                                onClick={() =>
                                    setFormData((prev) => {
                                        const existing = prev.figmaFiles || [];
                                        return {
                                            ...prev,
                                            figmaFiles: [...existing, newFigma()],
                                        };
                                    })
                                }
                            >
                                <span className="icon">
                                    <MdOutlineAddCircle />
                                </span>
                                <span>Add</span>
                            </AddDocButton>
                        </SectionTitle>

                        {formData.figmaFiles.map((file, idx) => (
                            <FigmaLinkSection
                                key={file.id}
                                ref={getFigmaRef(file.id)}
                                docId={idx}
                                fileUuid={file.id}
                                figmaLink={file.link}
                                initialMeta={file.meta}
                                onChange={updateFigmaLink}
                                onMetaFetched={handleFigmaMetaFetched}
                                onRemove={(id) => {
                                    setFormData((prev) => {
                                        const updated = prev.figmaFiles.filter((f) => f.id !== id);
                                        return { ...prev, figmaFiles: updated.length ? updated : [newFigma()] };
                                    });
                                }}
                                isDuplicate={formData.figmaFiles.some((f, i) => i !== idx && f.link === file.link)}
                                canRemove={formData.figmaFiles.length > 1}
                                isEmpty={file.link.trim() === ""}
                                onFetchStart={() => setFetchSummary(null)}
                            />
                        ))}
                    </>
                )}

                {hasAnyArtifactFetchEnabled && (
                    <Button
                    type="button"
                    onClick={fetchAllArtifacts}
                    disabled={fetchAllLoading || !hasAnyArtifactFetchEnabled}
                >
                    {fetchAllLoading ? "Fetching all..." : "Fetch"}
                </Button>
                )}

                {isSubmitting && (
                    <SavingOverlay>
                        <SavingBox>
                            <Spinner />
                            <SavingTitle>💾 Saving your session...</SavingTitle>
                            <SavingText>This may take a few seconds. Please don’t close this window.</SavingText>
                        </SavingBox>
                    </SavingOverlay>
                )}

                {fetchSummary && (
                    <FetchSummaryWrapper>
                        <SummaryItem $type="success">
                            <FaCheckCircle />
                            <span>{fetchSummary.successes} success</span>
                        </SummaryItem>

                        <SummaryItem $type="failure">
                            <FaTimesCircle />
                            <span>{fetchSummary.failures} failed</span>
                        </SummaryItem>
                    </FetchSummaryWrapper>
                )}

                {isSubmitting && (
                    <SavingOverlay>
                        <SavingBox>
                            <Spinner />
                            <SavingTitle>💾 Saving your session...</SavingTitle>
                            <SavingText>This may take a few seconds. Please don’t close this window.</SavingText>
                        </SavingBox>
                    </SavingOverlay>
                )}

                <ButtonGroup>
                    <CancelButton
                        type="button"
                        onClick={onCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </CancelButton>
                    <SubmitButton
                        type="submit"
                        disabled={isSubmitting}
                        $loading={isSubmitting}
                    >
                        Save
                    </SubmitButton>
                </ButtonGroup>
            </ContentWrapper>

            {showEditReason && (
                <EditReasonPopup
                    onSubmit={handleEditReasonSubmit}
                    onCancel={() => setShowEditReason(false)}
                />
            )}
        </FormContainer>
    );
}

export default AddActivityWindow;